// src/components/Control/Operator/NannyScreen.tsx
// Экран «Няня»: наблюдение за человеком и обстановкой. Собран из того, что
// робот уже присылает (новых протоколов нет):
//   - кто в кадре камеры 1 (robot/<id>/person; имена — узнавание по одежде),
//     список знакомых (robot/<id>/people) и лиц (vision/state);
//   - звуковая няня (нода audio: уровень, события, записи, «слушать вживую»);
//   - события зрения (падение, поза, знаки).
// Выбранный человек хранится только в этом браузере (nanny.target).
// Тревога: сильный звук / ключевое слово / тревога зрения или выбранный
// человек пропал из кадра дольше NANNY_LOST_MS — до «Всё в порядке».
import React, { useState } from 'react';
import { useEventsState, useRobot, useTelemetryState, useUiActions, useVoiceState } from '../robot/RobotContext';
import { RobotEvent } from '../robot/events';
import { useTick } from '../../../hooks/useTick';
import { putJson } from '../../../hooks/useZenohSubscription';
import { loadPref, savePref, timeOf } from '../../../types/features';
import { CameraView } from './Camera';
import { NannyToggle, NannyLevel, ListenLive, RecordingsList, useRecordings, useAudioState } from '../Features/NannyPanel';
import { RobotSpeech } from '../Features/VoicePanel';
import { ago } from './status';
import { Icon } from './ui';
import { StopButton } from './Header';

const NANNY_LOST_MS = 10000;
// Тревожные события старше этого срока уже не держат экран в тревоге
const ALERT_HOLD_MS = 5 * 60 * 1000;

const ICON: Record<string, string> = { nanny: '🔊', vision: '👁', person: '👤', robot: '🤖', voice: '🎙', mission: '🧭' };

function personWord(target: string) {
  return target || 'Человек';
}

const NannyScreen: React.FC<{ variant: 'desktop' | 'mobile' }> = ({ variant }) => {
  useTick(1000);
  const { session, robotId, events: eventsStore, voice, notify } = useRobot();
  const { openSettings, go, setCam, setLayout } = useUiActions();
  const [target, setTarget] = useState<string>(() => loadPref('nanny.target', ''));
  const { online, n } = useAudioState();
  const known = useTelemetryState(t => t.known);
  const faces = useTelemetryState(t => t.vision?.faces);
  const people = useTelemetryState(t => t.people);
  const personAt = useTelemetryState(t => t.personAt);
  const presence = useEventsState(e => e.presence);
  const ackAt = useEventsState(e => e.ackAt);
  const timeline = useEventsState(e => e.events.filter(ev => ['nanny', 'vision', 'person', 'robot'].includes(ev.source)).slice(0, 80));
  const speaking = useVoiceState(v => v.speaking);
  const rec = useRecordings();

  const names = Array.from(new Set([
    ...(known ?? []).map(k => k.name),
    ...Object.keys(faces ?? {}),
    ...Object.keys(presence.byName),
  ])).sort();

  const now = Date.now();
  const inFrameNow = now - personAt < 1500 ? people : [];
  const targetInFrame = target ? inFrameNow.some(p => p.known?.name === target) : inFrameNow.length > 0;
  const sessionSeen = target ? presence.byName[target] ?? 0 : presence.anyAt;
  const robotSeen = target ? (known ?? []).find(k => k.name === target)?.seen ?? 0 : 0;
  const lastSeen = Math.max(sessionSeen, robotSeen);
  const lostFor = targetInFrame ? 0 : lastSeen ? now - lastSeen : 0;

  // Причины тревоги
  const alerts: RobotEvent[] = timeline.filter(ev => ev.level === 'alert' && ev.at > ackAt && now - ev.at < ALERT_HOLD_MS);
  const lost = !!n?.enabled && sessionSeen > ackAt && !targetInFrame && lostFor > NANNY_LOST_MS;
  const attention = alerts.length > 0 || lost;

  const lastOf = (pred: (e: RobotEvent) => boolean) => timeline.find(pred);
  const sound = lastOf(e => e.source === 'nanny');
  const motion = lastOf(e => e.source === 'vision' && (e.kind === 'pose' || /сел|встал|упал|лёг|руки/i.test(e.text)));
  const danger = alerts[0];

  const call = () => {
    const who = target || 'эй';
    putJson(session, `robot/${robotId}/audio/cmd`, { cmd: 'say', text: `${who}, ты где? Отзовись, пожалуйста.` });
  };
  const openCamera = () => {
    setCam('0');
    if (variant === 'mobile') go('camera');
    else {
      setLayout('camera');
      go('map');
    }
  };
  const ack = () => eventsStore.set({ ackAt: Date.now() });
  // «Говорить»: голос оператора из динамика робота
  const toggleTalk = () => {
    if (speaking) voice.stopSpeak();
    else voice.startSpeak().catch(err => notify(`Нет доступа к микрофону: ${err instanceof Error ? err.message : String(err)}`, 'error'));
  };
  const chooseTarget = (v: string) => {
    setTarget(v);
    savePref('nanny.target', v);
  };

  const statusBlock = attention ? (
    <section className="op-nanny-alert" role="alert">
      <div className="op-nanny-state bad"><span aria-hidden="true">■</span> Требуется внимание</div>
      {lost && (
        <>
          <p className="op-nanny-reason">⚠️ {personWord(target)} не {target ? 'обнаружен(а)' : 'обнаружен'} в кадре</p>
          <p className="op-muted">Последнее обнаружение: {lastSeen ? ago(lastSeen) : '—'}</p>
        </>
      )}
      {alerts.slice(0, 3).map(a => (
        <p key={a.id} className="op-nanny-reason">⚠️ {a.text} <span className="op-muted">· {timeOf(a.at)}</span></p>
      ))}
      <div className="op-row wrap">
        <button type="button" className="op-btn primary" onClick={openCamera}><Icon name="camera" size={16} /> Открыть камеру</button>
        <button type="button" className="op-btn primary" onClick={call} disabled={!session}>Позвать</button>
        <button type="button" className="op-btn" onClick={ack}>Всё в порядке</button>
        <StopButton compact />
      </div>
    </section>
  ) : (
    <div className="op-nanny-state ok" role="status"><span aria-hidden="true">●</span> Спокойно</div>
  );

  const personCard = (
    <section className="op-card op-person" aria-label="Наблюдаемый человек">
      <div className="op-person-head">
        <span className="op-person-avatar" aria-hidden="true">👤</span>
        <div>
          <label className="op-person-select">
            <span className="op-muted">За кем следить</span>
            <select value={target} onChange={e => chooseTarget(e.target.value)} aria-label="Наблюдаемый человек">
              <option value="">любой человек в кадре</option>
              {names.map(nm => <option key={nm} value={nm}>{nm}</option>)}
            </select>
          </label>
          <div className="op-person-name">{personWord(target)}</div>
        </div>
      </div>
      <div className={`op-track ${targetInFrame ? 'ok' : lastSeen ? 'warn' : 'off'}`} role="status">
        <span aria-hidden="true">{targetInFrame ? '●' : lastSeen ? '▲' : '○'}</span>
        {targetInFrame ? 'В кадре — отслеживается' : lastSeen ? `Не в кадре · последний раз ${ago(lastSeen)}` : 'Пока не видели'}
      </div>
      {targetInFrame && sessionSeen > 0 && <div className="op-muted">Наблюдение с {timeOf(sessionSeen)}</div>}
      {!names.length && <p className="op-muted">Знакомых пока нет: запомните человека на экране камеры («Запомнить человека…») — тогда его можно выбрать здесь.</p>}
    </section>
  );

  const rows = (
    <section className="op-card op-nanny-rows" aria-label="Обстановка">
      <div className="op-kv"><span>Поза / движение</span><b>{motion ? `${motion.text} · ${ago(motion.at)}` : '—'}</b></div>
      <div className="op-kv"><span>Звук</span><b>{sound ? `${sound.text} · ${ago(sound.at)}` : n?.enabled ? 'тихо' : '—'}</b></div>
      <div className="op-kv"><span>Опасность</span><b className={danger ? 'bad' : ''}>{danger ? danger.text : 'Нет'}</b></div>
      <NannyLevel />
    </section>
  );

  const actions = (
    <section className="op-card" aria-label="Связь с человеком">
      <div className="op-row wrap">
        <ListenLive />
        <button type="button" className="op-btn" onClick={call} disabled={!session}>Позвать{target ? ` ${target}` : ''}</button>
        <button type="button" className="op-btn" disabled={!session}
                onClick={() => putJson(session, `robot/${robotId}/audio/cmd`, { cmd: 'stop_sound' })}>Замолчать</button>
      </div>
      <RobotSpeech compact />
    </section>
  );

  const timelineBlock = (
    <section className="op-card op-timeline" aria-label="События">
      <h3>События</h3>
      {timeline.length === 0 ? (
        <p className="op-muted">{n?.enabled ? 'Пока тихо.' : 'Включите режим няни, чтобы получать оповещения о звуках.'}</p>
      ) : (
        <ul>
          {timeline.map(ev => (
            <li key={ev.id} className={ev.level}>
              <time>{new Date(ev.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time>
              <span aria-hidden="true">{ICON[ev.source] ?? '•'}</span>
              <span className="text">{ev.text}{ev.levelDb !== undefined ? ` · ${ev.levelDb} дБ` : ''}</span>
              {ev.clip && <button type="button" className="btn-icon" aria-label="Прослушать запись события" onClick={() => rec.play(ev.clip!)}>▶</button>}
            </li>
          ))}
        </ul>
      )}
      <details>
        <summary>Записи звука</summary>
        <RecordingsList rec={rec} />
      </details>
    </section>
  );

  const head = (
    <div className="op-nanny-head">
      <h2>Режим няни</h2>
      <NannyToggle />
      {!online && session && <span className="op-muted">звук робота недоступен</span>}
      <span className="op-spacer" />
      <button type="button" className="op-btn" onClick={() => openSettings('nanny')}><Icon name="gear" size={16} /> Настройки няни</button>
    </div>
  );

  if (variant === 'mobile') {
    return (
      <div className="op-nanny mobile">
        {head}
        {personCard}
        <CameraView cam="0" className="op-nanny-cam" onClick={openCamera} />
        {statusBlock}
        {rows}
        {actions}
        {timelineBlock}
        <div className="op-nanny-talk">
          <button type="button" className={`op-btn big ${speaking ? 'active' : 'primary'}`} aria-pressed={speaking}
                  disabled={!session} onClick={toggleTalk}>
            <Icon name="mic" size={18} /> {speaking ? 'Вас слышно из робота — закончить' : 'Говорить'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="op-nanny desktop">
      {head}
      <div className="op-nanny-grid">
        <div className="op-nanny-left">
          {personCard}
          <CameraView cam="0" className="op-nanny-cam" />
        </div>
        <div className="op-nanny-right">
          {statusBlock}
          {rows}
          {actions}
          {timelineBlock}
        </div>
      </div>
    </div>
  );
};

export default NannyScreen;
